import { css } from '@emotion/react'
import styled from '@emotion/styled'
import SearchIcon from '@mui/icons-material/Search'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { headerSearch } from '../../../actions/search'
import { MessageEvent, SecureStateType } from '../../../constants/enums'
import { MAX_SEARCH_QUERIES_STORED } from '../../../constants/search'
import {
    isLevelModel,
    isLevelPremium,
} from '../../../mfcLogic/userProperties/level'
import {
    openAccountDrawer,
    openAuthenticationDrawer,
    openSignupDrawer,
    openTokensDrawer,
} from '../../../state/modals/state.drawer'
import { pubSubMessages, pubSubNotes } from '../../../state/pubSub'
import { updateLayoutState } from '../../../state/state.layout'
import {
    deleteSearchQueryExternal,
    resetSearchQuery,
    resetSearchResults,
} from '../../../state/state.search'
import { toggleSidebarVisibility } from '../../../state/state.sidebar'
import i18n from '../../../utilities/i18n'
import Button from '../../Buttons/Button'
import ButtonBuy from '../../Buttons/ButtonBuy'
import ButtonMenu from '../../Buttons/ButtonMenu'
import ButtonSidebar from '../../Buttons/ButtonSidebar'
import ButtonUserBroadcast from '../../Buttons/ButtonUserBroadcast'
import IconButton from '../../Buttons/IconButton'
import { AppStateType, useAppState } from '../../Hooks/useAppState'
import BadgeAvatar from '../../common/BadgeAvatar'
import DarkModeToggle from '../../common/DarkModeToggle'
// import { useSearch } from '../../Hooks/useSearch'
import { transitionNavLayout } from '../../../actions/layout'
import { setScreenOrientation } from '../../../app-state/state.screen'
import { AppRouter } from '../../../state/state.router'
import { isIDevice, isPWA } from '../../../utilities/browser'
import { routerPush } from '../../../utilities/router'
import { useLayoutSelector } from '../../Hooks/useLayout'
import { useRouteSelector } from '../../Hooks/useRoute'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useSearch } from '../../Hooks/useSearch'
import { useSecureState } from '../../Hooks/useSecureState'
import { useSidebarSelector } from '../../Hooks/useSidebar'
import { useUserSelector } from '../../Hooks/useUser'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS, { minWindowWidth } from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import { COMPONENT_HEIGHTS } from '../../theme'
import AlertTooltips from '../AlertTooltips'
import InputSearch from '../InputSearch'

const EXPANDED_MOBILE_SEARCH_HEIGHT = COMPONENT_HEIGHTS.inputSearch + 8

const sharedLinkAttributes = {
    flex: 'auto',
    alignItems: 'flex-start',
    textDecoration: 'none',
} as const

const StyledLogo = styled('img')({
    height: 14,
    marginTop: 1,
    marginLeft: 0,
    marginRight: 'auto',
    cursor: 'pointer',
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        height: 16,
    },
    '@media only screen and (max-width: 410px)': {
        height: 14,
    },
    '@media only screen and (max-width: 405px)': {
        height: 13,
    },
    '@media only screen and (max-width: 395px)': {
        height: 12,
    },
    '@media only screen and (max-width: 380px)': {
        height: 11,
    },
    '@media only screen and (max-width: 367px)': {
        height: 10,
    },
})

const StyledSearchContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: EXPANDED_MOBILE_SEARCH_HEIGHT,
    minHeight: EXPANDED_MOBILE_SEARCH_HEIGHT,
    width: '100%',
    padding: '0 8px',
})
const StyledAccountContainer = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    flex: 'none',
    alignItems: 'center',
    justifyContent: 'right',
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        justifyContent: 'left',
    },
})
const StyledButtonsContainer = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    flex: 'none',
    alignItems: 'center',
    justifyContent: 'left',
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        justifyContent: 'right',
    },
    '@media only screen and (max-width: 410px)': {
        '& svg ': {
            height: '1.3rem',
            width: '1.3rem',
        },
    },
    '@media only screen and (max-width: 400px)': {
        '& svg ': {
            height: '1.25rem',
            width: '1.25rem',
        },
    },
})

const StyledMenuButtonContainer = styled('div')({
    display: 'none',
    marginLeft: 0,
    marginRight: 16,
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        display: 'block',
    },
})

export const textStyles = {
    lineHeight: 1.5,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 14,
    '@media only screen and (max-width: 470px)': {
        fontSize: 12,
    },
} as const

const responsiveSmallTextStyles = {
    '@media only screen and (max-width: 440px)': {
        fontSize: 11,
    },
    '@media only screen and (max-width: 435px)': {
        fontSize: 10,
    },
}

const StyledLoginRegisterContainer = styled('div')(textStyles)
const StyledNameTokensContainer = styled('div')({
    ...textStyles,
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        display: 'flex',
        flexFlow: 'column nowrap',
        textAlign: 'right',
        marginLeft: getThemeSpacing(1),
        marginRight: getThemeSpacing(1),
        lineHeight: 1.2,
    },
})
const StyledNameSpan = styled('span')({
    ...responsiveSmallTextStyles,
    color: palette.headerText,
})
const StyledTokens = styled('span')({
    ...sharedLinkAttributes,
    ...responsiveSmallTextStyles,
    color: palette.headerTokens,
    marginLeft: getThemeSpacing(1),
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        fontSize: 12,
        marginLeft: 0,
    },
    '&:hover': {
        textDecoration: 'underline',
    },
})
const StyledLoginSpan = styled('span')({
    ...sharedLinkAttributes,
    marginRight: 15,
})
const StyledSignupSpan = styled('span')({
    ...sharedLinkAttributes,
    color: palette.headerText,
})
const loginButtonStyles = css({
    color: palette.headerText,
    minWidth: 64,
    marginLeft: getThemeSpacing(1.75),
    marginRight: getThemeSpacing(1.75),
    padding: `${getThemeSpacing(0.5)} ${getThemeSpacing(0.625)}`,
    '&:hover': {
        backgroundColor: palette.activeHover,
    },
})
const signupButtonStyles = css({
    color: palette.headerText,
    padding: `${getThemeSpacing(0.5)} ${getThemeSpacing(0.625)}`,
    '&:hover': {
        backgroundColor: palette.activeHover,
    },
})
const iconButtonStyles = css({
    padding: 0,
    border: 0,
    marginRight: 6,
})
const StyledSearchIcon = styled(SearchIcon)({
    color: palette.contrastIcons,
})

const sidebarButtonStyles = css({
    marginRight: 4,
})

const darkModeToggleStyles = css({
    marginLeft: getThemeSpacing(1),
})

const onLogin = () => openAuthenticationDrawer()
const onSignup = () => openSignupDrawer()
const onOpenAccount = () => openAccountDrawer()
const onPurchaseTokens = () => openTokensDrawer()
const onToggleSidebar = () => {
    // update the window content size
    toggleSidebarVisibility()
    setScreenOrientation({ overrideSkip: true })
}

interface ILogo {
    darkMode: boolean
    ageGateActive: boolean
}

const Logo = memo(({ darkMode, ageGateActive }: ILogo) => {
    const onClick = (e) => {
        e.preventDefault()
        if (ageGateActive) return
        if (AppRouter.route.visibleRoute === '/') {
            const modelGrid = document.querySelector('.live-preview-grid')
            if (modelGrid) {
                modelGrid.scroll(0, 0)
            }
        } else {
            routerPush('/')
        }
    }
    return (
        <StyledLogo
            alt="MyFreeCams.com"
            onClick={onClick}
            src={
                !darkMode
                    ? '/static/mfc-app-logo-green-transparent-bg.svg'
                    : '/static/mfc-app-logo-white.svg'
            }
        />
    )
})

interface IHeaderSearch {
    onInputSearchChange(query?: string): void
    onClear(): void
    mobile?: boolean
    page: string
}

const HeaderSearch = memo((props: IHeaderSearch) => {
    const { onInputSearchChange, onClear, mobile, page } = props
    const isMessagesPage = page === 'messages'
    const isNotesPage = page === 'notes'
    const isHomePage = page === ''

    const { query: searchQuery } = useSearch()
    const { search: stateSearchQuery, external } = searchQuery

    const searchInputRef = useRef(null)

    const onInputRefCallback = useCallback((ref) => {
        searchInputRef.current = ref
    }, [])

    useEffect(() => {
        if (!external) {
            return
        }

        searchInputRef.current.value = stateSearchQuery

        deleteSearchQueryExternal()
    }, [external])

    return (
        <div
            data-testid="StyledInputSearchContainer"
            style={{
                flex: mobile ? 0 : '0 1 265px',
                maxHeight: '100%',
                marginLeft: mobile ? 0 : 2,
                background: palette.headerBackground,
                width: mobile ? '100%' : 'auto',
            }}
        >
            <InputSearch
                page={page}
                autoFocus={true}
                debounce={true}
                debounceDelay={isHomePage ? 325 : 500}
                defaultValue={''}
                onInputRef={onInputRefCallback}
                placeholder={
                    isMessagesPage
                        ? i18n.t('header.searchMessages')
                        : isNotesPage
                          ? i18n.t('header.searchNotes')
                          : i18n.t('header.search')
                }
                historyMax={MAX_SEARCH_QUERIES_STORED}
                onChange={onInputSearchChange}
                onClear={onClear}
                quietFocus
                style={{ borderRadius: shape.borderRadius }}
            />
        </div>
    )
})

interface IHeader {
    authenticated: boolean
    darkMode: boolean
    isMobile: boolean
    level: number
    showMenu: boolean
    uid: number
    username: string
    vertical: boolean
}

const Header = memo((props: IHeader) => {
    const { showMenu, vertical } = props
    const {
        authenticated = props.authenticated,
        darkMode = props.darkMode,
        isMobile = props.isMobile,
    } = useAppState(AppStateType.APP)
    const tokens = useUserSelector('me', 'tokens')
    const username = useUserSelector('me', 'name') || props.username
    const level = useUserSelector('me', 'level') || props.level
    const uid = useUserSelector('me', 'id') || props.uid

    const { ageGateActive } = useSecureState(SecureStateType.AGE_VERIFICATION)
    const [expandSearch, setExpandSearch] = useState(false)
    const [compactHeader, setCompactHeader] = useState(isMobile)
    const [isiOSMobilePWALandscape, setIsiOSMobilePWALandscape] =
        useState(false)
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>() // so SSR and client renders match
    const [isDarkMode, setIsDarkMode] = useState<boolean>() // so SSR and client renders match
    const expandedNav = useLayoutSelector('expandedNav')
    const expandedSearchMobile = useLayoutSelector('expandedSearchMobile')

    const page = useRouteSelector('visiblePage')
    const isSearchPage = page === 'search'

    useEffect(() => {
        setExpandSearch(false)
        updateLayoutState({
            expandedSearchMobile: false,
        })
    }, [page])

    const windowWidth = useScreenStateSelector('windowWidth')

    const smallViewport =
        windowWidth > 0 ? windowWidth < APP_BREAKPOINTS.md : isMobile
    const expanded = expandedSearchMobile
    const headerHeight = compactHeader
        ? COMPONENT_HEIGHTS.headerCompact
        : COMPONENT_HEIGHTS.headerDesktop
    const height = headerHeight + (expanded ? EXPANDED_MOBILE_SEARCH_HEIGHT : 0)

    // set the correct state in the first render (client side)
    useEffect(() => {
        setIsDarkMode(darkMode)
    }, [darkMode])

    // set the correct state in the first render (client side)
    useEffect(() => {
        setCompactHeader(smallViewport)
    }, [smallViewport])

    const sidebarOpen = useSidebarSelector('show')

    // set the correct state in the first render (client side)
    useEffect(() => {
        setIsSidebarOpen(sidebarOpen)
    }, [sidebarOpen])

    // , {
    //     // this should probably be handled here so we can convert the hook otherwise
    //     ssrCompatible: true,
    // })
    const [delayedTokens, setTokens] = useState(null)
    useEffect(() => {
        // let the first render have blank tokens so it matches ssr
        setTokens(tokens)
    }, [tokens])

    useEffect(() => {
        setIsiOSMobilePWALandscape(
            isPWA && isIDevice && compactHeader && !vertical,
        )
    }, [compactHeader, vertical])

    const isModel = isLevelModel(level)
    const isPremium = isLevelPremium(level)

    const onExpandNav = useCallback(() => {
        transitionNavLayout(expandedNav)
    }, [expandedNav])

    const onToggleSearch = useCallback(() => setExpandSearch((val) => !val), [])
    const onToggleSearchMobile = useCallback(() => {
        updateLayoutState({
            expandedSearchMobile: !expandedSearchMobile,
        })
    }, [expandedSearchMobile])

    const onClear = useCallback(() => {
        setExpandSearch(false)
        updateLayoutState({
            expandedSearchMobile: false,
        })
        const isMessagesPage = page === 'messages'
        const isNotesPage = page === 'notes'

        if (isMessagesPage) {
            pubSubMessages.emit(MessageEvent.SEARCH, null)
        }
        if (isNotesPage) {
            pubSubNotes.emit('search', null)
        }
        resetSearchQuery()
        resetSearchResults()
    }, [page])

    const MobileSearchButton = memo(() => (
        <IconButton css={iconButtonStyles} onClick={onToggleSearchMobile}>
            <StyledSearchIcon data-testid="SearchIcon" />
        </IconButton>
    ))

    const Buttons = () => (
        <StyledButtonsContainer>
            {!ageGateActive &&
                compactHeader &&
                !isiOSMobilePWALandscape &&
                !isSearchPage && <MobileSearchButton />}
            {!ageGateActive &&
                !compactHeader &&
                !isSearchPage &&
                !expandSearch && (
                    <IconButton onClick={onToggleSearch} title="Search">
                        <StyledSearchIcon data-testid="SearchIcon" />
                    </IconButton>
                )}
            {!ageGateActive &&
                !compactHeader &&
                !isSearchPage &&
                expandSearch && (
                    <HeaderSearch
                        onInputSearchChange={headerSearch}
                        onClear={onClear}
                        page={page}
                    />
                )}
            {!ageGateActive && !compactHeader && !isModel && <ButtonBuy />}
            {!compactHeader && (
                <DarkModeToggle
                    container="header"
                    styleOverrides={darkModeToggleStyles}
                />
            )}
            {!ageGateActive && authenticated && isPremium && (
                <ButtonUserBroadcast
                    uid={uid}
                    compact={isiOSMobilePWALandscape}
                />
            )}
            {!ageGateActive && !compactHeader && authenticated && (
                <ButtonSidebar
                    filled={isSidebarOpen}
                    title={
                        isSidebarOpen
                            ? i18n.t('header.closeSidebar')
                            : i18n.t('header.openSidebar')
                    }
                    data-testid={`IconButton__FriendsToggle${
                        isSidebarOpen ? 'Open' : 'Close'
                    }`}
                    size={26.5}
                    overrides={sidebarButtonStyles}
                    onClick={ageGateActive ? () => {} : onToggleSidebar}
                />
            )}
        </StyledButtonsContainer>
    )

    const Account = () => {
        const UserInfoSection = (
            <>
                <StyledNameTokensContainer data-testid="Header__UsernameTypography">
                    <StyledNameSpan
                        onClick={ageGateActive ? () => {} : onOpenAccount}
                        title={i18n.t('header.openMenu')}
                    >
                        {username}
                    </StyledNameSpan>
                    {authenticated && !isModel && (
                        <StyledTokens
                            data-testid="Header__TokensCount"
                            onClick={
                                ageGateActive ? () => {} : onPurchaseTokens
                            }
                            title={i18n.t('header.purchaseTokens')}
                        >
                            {delayedTokens ?? ''} {i18n.t('header.tokens')}
                        </StyledTokens>
                    )}
                </StyledNameTokensContainer>
                {!compactHeader && (
                    <BadgeAvatar
                        id={uid}
                        offline={false}
                        disableBadge={true}
                        broadcasting={false}
                        addBoxShadow={true}
                        onClick={ageGateActive ? () => {} : onOpenAccount}
                        title={i18n.t('header.openMenu')}
                        size={COMPONENT_HEIGHTS.headerDesktop - 10}
                    />
                )}
            </>
        )
        const CompactLoginRegisterSection = (
            <StyledLoginRegisterContainer data-testid="Header__UsernameTypography">
                <StyledLoginSpan
                    data-testid="Header__LoginButton"
                    onClick={onLogin}
                >
                    {i18n.t('header.login')}
                </StyledLoginSpan>
                <StyledSignupSpan
                    data-testid="Header__RegisterButton"
                    onClick={onSignup}
                >
                    {i18n.t('header.freeAccount')}
                </StyledSignupSpan>
            </StyledLoginRegisterContainer>
        )
        const LoginRegisterSection = (
            <StyledLoginRegisterContainer data-testid="Header__UsernameTypography">
                <Button
                    data-testid="Header__LoginButton"
                    onClick={onLogin}
                    title={i18n.t('header.login')}
                    css={loginButtonStyles}
                    size="small"
                    border
                    clean
                >
                    {i18n.t('header.login')}
                </Button>
                <Button
                    data-testid="Header__RegisterButton"
                    onClick={onSignup}
                    title={i18n.t('header.freeAccount')}
                    css={signupButtonStyles}
                    size="small"
                    border
                    clean
                >
                    {i18n.t('header.freeAccount')}
                </Button>
            </StyledLoginRegisterContainer>
        )
        return (
            <StyledAccountContainer>
                {authenticated
                    ? UserInfoSection
                    : compactHeader
                      ? CompactLoginRegisterSection
                      : LoginRegisterSection}
            </StyledAccountContainer>
        )
    }

    return (
        <div
            css={{
                background: palette.headerBackground,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                ...(isiOSMobilePWALandscape
                    ? { paddingLeft: 58, paddingRight: 36 }
                    : null),
                height: height,
                minHeight: height,
            }}
        >
            <header
                css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: compactHeader
                        ? 'flex-end'
                        : 'space-between',
                    width: '100%',
                    height: headerHeight,
                    minHeight: headerHeight,
                    padding: compactHeader ? '0 4px' : '0 8px',
                    background: palette.headerBackground,
                    WebkitFontSmoothing: 'auto',
                }}
            >
                {!compactHeader && showMenu && (
                    <StyledMenuButtonContainer>
                        <ButtonMenu
                            onClick={ageGateActive ? () => {} : onExpandNav}
                        />
                    </StyledMenuButtonContainer>
                )}
                <Logo darkMode={isDarkMode} ageGateActive={ageGateActive} />
                {authenticated && (
                    <AlertTooltips addLeftMargin={isiOSMobilePWALandscape} />
                )}
                {Buttons()}
                {Account()}
            </header>
            {expandedSearchMobile && (
                <StyledSearchContainer>
                    <HeaderSearch
                        onInputSearchChange={headerSearch}
                        onClear={onClear}
                        page={page}
                        mobile
                    />
                </StyledSearchContainer>
            )}
        </div>
    )
})

export default Header
